import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../context/AuthContext.js";
import { getUserProfile, updateUserProfile } from "../api/users.js";
import { uploadAvatarFile, updateProfileWithImage, uploadAvatarUrl } from "../api/avatar.js";
import SEO from "../components/SEO.js";
import "../index.css";

// Modern professional profile component with backend integration
export default function ProfilePage() {
  const { user, refreshUserData } = useAuth();
  const [userData, setUserData] = useState(null);
  const [editData, setEditData] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [selectedFile, setSelectedFile] = useState(null);
  const [avatarUrl, setAvatarUrl] = useState("");
  const [error, setError] = useState("");

  // Fetch profile data on mount
  useEffect(() => {
    if (user?.id) {
      fetchProfileData();
    }
  }, [user?.id]);

  const fetchProfileData = async () => {
    try {
      setFetchLoading(true);
      setError("");
      console.log('Profile: Fetching profile data for user:', user.id);

      const profileData = await getUserProfile(user.id);
      console.log('Profile: Successfully fetched profile data:', profileData);

      setUserData(profileData);
      setEditData(profileData);
      setAvatarUrl(profileData.avatar || "");
    } catch (err) {
      console.error("Profile: Error fetching profile:", err);
      console.error("Profile: Error details:", err.response?.data || err.message);

      // Enhanced error handling with specific error messages
      let errorMessage = "Failed to load profile data. Please try again.";

      if (err.response) {
        const status = err.response.status;
        const data = err.response.data;

        if (status === 401) {
          errorMessage = "Authentication required. Please log in again.";
        } else if (status === 403) {
          errorMessage = "Access denied. You don't have permission to view this profile.";
        } else if (status === 404) {
          errorMessage = "Profile not found. It may not exist yet.";
        } else if (status >= 500) {
          errorMessage = "Server error. Please try again later.";
        } else if (data?.message) {
          errorMessage = data.message;
        }
      } else if (err.request) {
        errorMessage = "Network error. Please check your connection and try again.";
      }

      setError(errorMessage);

      // Improved fallback: use user data from context with better defaults
      if (user) {
        console.log('Profile: Using fallback user data from context');
        const fallbackData = {
          id: user.id,
          firstName: user.firstName || user.name?.split(' ')[0] || '',
          lastName: user.lastName || user.name?.split(' ').slice(1).join(' ') || '',
          name: user.name || user.displayName || `${user.firstName || ''} ${user.lastName || ''}`.trim(),
          email: user.email || '',
          bio: user.bio || '',
          avatar: user.avatar || '',
          planStatus: user.planStatus || 'Free',
          socialLinks: user.socialLinks || { twitter: '', linkedin: '' },
          preferences: user.preferences || { theme: 'dark' },
          createdAt: user.createdAt || new Date().toISOString(),
          updatedAt: user.updatedAt || new Date().toISOString()
        };

        setUserData(fallbackData);
        setEditData(fallbackData);
        setAvatarUrl(fallbackData.avatar || "");
      }
    } finally {
      setFetchLoading(false);
    }
  };

  // Handle file select
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  // Upload avatar file
  const handleAcceptUpload = async () => {
    if (!selectedFile) {
      setError("Please select an image first.");
      return;
    }

    try {
      setLoading(true);
      setError("");
      const uploadResult = await uploadAvatarFile(selectedFile);
      if (uploadResult.imageUrl) {
        setAvatarUrl(uploadResult.imageUrl);
        await updateProfileWithImage(uploadResult.imageUrl);
        // Refresh user data to get updated profile
        await refreshUserData();
        alert("✅ Avatar uploaded successfully!");
      }
    } catch (err) {
      console.error("Upload error:", err);
      setError("Failed to upload avatar. Please try again.");
    } finally {
      setLoading(false);
      setSelectedFile(null);
    }
  };

  // Save via URL
  const handleUrlSave = async () => {
    if (!avatarUrl.trim()) {
      setError("Please enter a valid image URL.");
      return;
    }

    try {
      setLoading(true);
      setError("");
      const uploadResult = await uploadAvatarUrl(avatarUrl);
      if (uploadResult.imageUrl) {
        setAvatarUrl(uploadResult.imageUrl);
        await updateProfileWithImage(uploadResult.imageUrl);
        // Refresh user data to get updated profile
        await refreshUserData();
        alert("✅ Avatar URL uploaded and saved!");
      }
    } catch (err) {
      console.error("URL save error:", err);
      setError("Failed to upload and save avatar URL. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Input change
  const handleEditChange = (field, value) => {
    if (field.includes('.')) {
      // Handle nested fields like socialLinks.twitter
      const [parent, child] = field.split('.');
      setEditData((prev) => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setEditData((prev) => ({ ...prev, [field]: value }));
    }
  };

  // Save profile changes
  const handleSaveProfile = async () => {
    try {
      setLoading(true);
      setError("");
      await updateUserProfile(editData);
      setUserData(editData);
      setIsEditing(false);
      // Refresh user data in context
      await refreshUserData();
      alert("✅ Profile updated successfully!");
    } catch (err) {
      console.error("Update failed:", err);
      setError("Failed to update profile. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Cancel edit
  const handleCancelEdit = () => {
    setEditData(userData);
    setIsEditing(false);
    setError("");
  };

  if (fetchLoading) {
    return (
      <div className="profile-container">
        <p>Loading profile...</p>
      </div>
    );
  }

  if (!userData) {
    return (
      <div className="profile-container">
        <p>No profile data available. Please try logging in again.</p>
      </div>
    );
  }

  return (
    <>
      <SEO
        title="User Profile - RapidMind AI Dashboard"
        description="Manage your RapidMind AI profile. Update personal information, avatar, social links, and account preferences. Customize your AI workspace experience."
        keywords="user profile, rapidmind ai profile, account settings, avatar upload, social links, AI preferences, user dashboard"
        image="/RapidMind 2025.png"
        type="website"
      />
      <div className="profile-container">
        <h1 className="profile-title"> My Profile</h1>

      {error && (
        <div style={{
          backgroundColor: "#fee",
          color: "#c33",
          padding: "1rem",
          borderRadius: "8px",
          marginBottom: "1rem",
          border: "1px solid #fcc"
        }}>
          {error}
        </div>
      )}

      {/* Avatar Section */}
      <div className="profile-card">
        <div className="avatar-container">
          <div className="avatar-circle-wrapper">
            {avatarUrl ? (
              <img
                src={avatarUrl}
                alt="User Avatar"
                className="profile-avatar"
                onError={(e) => {
                  e.target.src = "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgdmlld0JveD0iMCAwIDEwMCAxMDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjEwMCIgaGVpZ2h0PSIxMDAiIGZpbGw9IiNFMEUwRTAiLz48Y2lyY2xlIGN4PSI1MCIgY3k9IjM1IiByPSIyMCIgZmlsbD0iI0ZGRiIvPjxjaXJjbGUgY3g9IjUwIiBjeT0iNzUiIHI9IjI1IiBmaWxsPSIjQkJCIi8+PC9zdmc+Cg==";
                }}
              />
            ) : (
              <div style={{
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "var(--panel-dark)",
                color: "#888",
                borderRadius: "50%"
              }}>
                No Avatar
              </div>
            )}
          </div>

          <div className="avatar-actions">
            <div style={{ marginBottom: "1rem" }}>
              <input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                style={{ marginBottom: "0.5rem", display: "block" }}
              />
              <button
                className="btn-avatar"
                onClick={handleAcceptUpload}
                disabled={loading || !selectedFile}
              >
                {loading ? "Uploading..." : "Upload Avatar"}
              </button>
            </div>

            <div>
              <input
                type="url"
                placeholder="https://example.com/avatar.jpg"
                value={avatarUrl}
                onChange={(e) => setAvatarUrl(e.target.value)}
                style={{
                  width: "100%",
                  padding: "0.5rem",
                  border: "1px solid #444",
                  borderRadius: "8px",
                  backgroundColor: "#2b2b2b",
                  color: "#ffffff",
                  marginBottom: "0.5rem"
                }}
              />
              <button
                className="btn-avatar"
                onClick={handleUrlSave}
                disabled={loading}
              >
                {loading ? "Saving..." : "Save URL"}
              </button>
            </div>
          </div>
        </div>

        {/* Info Section */}
        {!isEditing ? (
          <div className="profile-info">
            <div>
              <label>Name:</label>
              <p>{userData.name || userData.firstName + " " + userData.lastName || "Not set"}</p>
            </div>
            <div>
              <label>Email:</label>
              <p>{userData.email || "Not set"}</p>
            </div>
            <div>
              <label>Bio:</label>
              <p>{userData.bio || "No bio yet"}</p>
            </div>
            <div>
              <label>Member Since:</label>
              <p>{userData.createdAt ? new Date(userData.createdAt).toLocaleDateString() : "Unknown"}</p>
            </div>
            <div>
              <label>Plan:</label>
              <p className="plan">{userData.planStatus || "Free"}</p>
            </div>

            <div className="profile-actions">
              <button className="btn" onClick={() => setIsEditing(true)}>
                Edit Profile
              </button>
            </div>
          </div>
        ) : (
          <div className="edit-profile-form">
            <h3>Edit Profile</h3>

            <div className="form-group">
              <label htmlFor="firstName">First Name:</label>
              <input
                id="firstName"
                type="text"
                value={editData.firstName || ""}
                onChange={(e) => handleEditChange("firstName", e.target.value)}
              />
            </div>

            <div className="form-group">
              <label htmlFor="lastName">Last Name:</label>
              <input
                id="lastName"
                type="text"
                value={editData.lastName || ""}
                onChange={(e) => handleEditChange("lastName", e.target.value)}
              />
            </div>

            <div className="form-group">
              <label htmlFor="email">Email:</label>
              <input
                id="email"
                type="email"
                value={editData.email || ""}
                onChange={(e) => handleEditChange("email", e.target.value)}
              />
            </div>

            <div className="form-group">
              <label htmlFor="bio">Bio:</label>
              <textarea
                id="bio"
                rows="3"
                value={editData.bio || ""}
                onChange={(e) => handleEditChange("bio", e.target.value)}
              />
            </div>

            <div className="form-group">
              <label htmlFor="twitter">Twitter:</label>
              <input
                id="twitter"
                type="url"
                placeholder="https://twitter.com/username"
                value={editData.socialLinks?.twitter || ""}
                onChange={(e) => handleEditChange("socialLinks.twitter", e.target.value)}
              />
            </div>

            <div className="form-group">
              <label htmlFor="linkedin">LinkedIn:</label>
              <input
                id="linkedin"
                type="url"
                placeholder="https://linkedin.com/in/username"
                value={editData.socialLinks?.linkedin || ""}
                onChange={(e) => handleEditChange("socialLinks.linkedin", e.target.value)}
              />
            </div>

            <div className="form-group">
              <label htmlFor="theme">Theme:</label>
              <select
                id="theme"
                value={editData.preferences?.theme || "dark"}
                onChange={(e) => handleEditChange("preferences.theme", e.target.value)}
              >
                <option value="light">Light</option>
                <option value="dark">Dark</option>
              </select>
            </div>

            <div className="form-actions">
              <button
                className="btn"
                onClick={handleSaveProfile}
                disabled={loading}
              >
                {loading ? "Saving..." : "Save Changes"}
              </button>
              <button
                className="btn danger"
                onClick={handleCancelEdit}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
    </>
  );
}
